/**
 * Members-only gate for reading surfaces.
 *
 * Publications, chapters and comments are readable by signed-in members only
 * (enforced in the database, not just here). Signed-out visitors get a clear
 * explanation and a route to joining rather than an empty page.
 */

import { Link, useRouterState } from "@tanstack/react-router";
import { BookLock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function MembersOnlyGate({
  title = "Members only",
  description = "Quibblies is an 18+ community. Writing, chapters and comments are only visible to signed-in members.",
}: {
  title?: string;
  description?: string;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <Card className="mx-auto max-w-xl">
      <CardHeader className="text-center">
        <BookLock className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
        <CardTitle className="font-display text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <div className="flex flex-wrap justify-center gap-2">
          <Button asChild>
            <Link to="/auth" search={{ redirect: pathname }}>
              Sign in to read
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/auth" search={{ redirect: pathname, mode: "register" as const }}>
              Create an account
            </Link>
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground">
          You must be 18 or over to join. Read our{" "}
          <Link to="/guidelines" className="underline">
            community guidelines
          </Link>{" "}
          before signing up.
        </p>
      </CardContent>
    </Card>
  );
}
